import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// ====== Import schemas from generated ======
const {
  OrganizationFirestoreResponse,
  UpdateOrganizationDto,
  ResetDataResponseDto,
  CurrencyDto,
  BusinessTypeDto,
  SubscriptionSyncResponseDto,
  OverrideSubscriptionDto,
} = schemas;

// ====== Re-export sub-schemas for external use ======
export const CurrencySchema = CurrencyDto;
export const BusinessTypeSchema = BusinessTypeDto;

// ====== Comparison Schema (not in generated) ======
const OrgFieldDifference = z.object({
  field: z.string(),
  supabaseValue: z.unknown(),
  firestoreValue: z.unknown(),
  reason: z.string().optional(),
}).passthrough();

export const OrganizationComparisonResultSchema = z.object({
  matches: z.boolean(),
  supabaseId: z.string().nullable(),
  firestoreId: z.string(),
  existsInSupabase: z.boolean(),
  existsInFirestore: z.boolean(),
  differences: z.array(OrgFieldDifference),
  warnings: z.array(z.string()),
}).passthrough();

// ====== Subscription (ATL-855) ======
/**
 * Both come straight from the generated spec — never hand-defined, which is how these drift.
 *
 * Note there is deliberately no request schema for `syncSubscription`: the endpoint accepts no
 * plan value at all. That is the whole design (see
 * `apps/atlas-backend/src/modules/organizations/organization-subscription.service.ts`), and the
 * absence of a body DTO in the generated spec is the machine-checkable statement of it.
 */
export const SubscriptionSyncResultSchema = SubscriptionSyncResponseDto;
export const OverrideSubscriptionSchema = OverrideSubscriptionDto;

export type SubscriptionSyncResult = z.infer<typeof SubscriptionSyncResponseDto>;
export type OverrideSubscription = z.infer<typeof OverrideSubscriptionDto>;

// ====== Re-export schemas for external use ======
export const OrganizationResponseSchema = OrganizationFirestoreResponse;
export const UpdateOrganizationSchema = UpdateOrganizationDto;

// ====== Type Exports ======
export type OrganizationResponse = z.infer<typeof OrganizationFirestoreResponse>;
export type UpdateOrganization = z.infer<typeof UpdateOrganizationDto>;
export type OrganizationComparisonResult = z.infer<typeof OrganizationComparisonResultSchema>;

/**
 * Create organizations API factory
 */
export function createOrganizationsApi(client: TypedApiClient) {
  return {
    /**
     * Get current organization
     */
    getCurrentOrganization: (): Promise<OrganizationResponse> => {
      return client.get('/organizations', {
        responseSchema: OrganizationFirestoreResponse,
      });
    },

    /**
     * Get organization by ID
     */
    getOrganizationById: (id: string): Promise<OrganizationResponse> => {
      return client.get(apiPath`/organizations/${id}`, {
        responseSchema: OrganizationFirestoreResponse,
      });
    },

    /**
     * Update organization
     */
    updateOrganization: (id: string, data: UpdateOrganization): Promise<OrganizationResponse> => {
      return client.put(apiPath`/organizations/${id}`, {
        data,
        requestSchema: UpdateOrganizationDto,
        responseSchema: OrganizationFirestoreResponse,
      });
    },

    /**
     * Update onboarding state
     */
    updateOnboarding: (id: string, data: { onboardingChecklist?: unknown[]; onboardingTasksLeft?: number; onboardingCompleted?: boolean }): Promise<OrganizationResponse> => {
      return client.patch(apiPath`/organizations/${id}/onboarding`, {
        data,
        responseSchema: OrganizationFirestoreResponse,
      });
    },

    /**
     * Set the per-org READ-cutover gate (ATL-462).
     * enabled=true → this org reads from Supabase; false → reads from Firestore (default/kill-switch).
     */
    setSupabaseReads: (id: string, enabled: boolean): Promise<OrganizationResponse> => {
      return client.patch(apiPath`/organizations/${id}/supabase-reads`, {
        data: { enabled },
        responseSchema: OrganizationFirestoreResponse,
      });
    },

    /**
     * Update the stored billing-gate override (ATL-615): liftBlockTill. Dual-write target for the
     * admin "unlock" so the override survives the Phase-5 read cutover. currentMonthSalesUSD is NOT
     * here — it is computed server-side. Pass liftBlockTill: null to clear the override.
     */
    updateBillingUsage: (
      id: string,
      data: { liftBlockTill?: string | null },
    ): Promise<OrganizationResponse> => {
      return client.patch(apiPath`/organizations/${id}/billing-usage`, {
        data,
        responseSchema: OrganizationFirestoreResponse,
      });
    },

    /**
     * ATL-855 — reconcile this organization's subscription with what the SERVER can verify.
     *
     * Takes no plan. The backend derives the entitlement from the provider webhooks it has stored
     * (`revenuecat_events` / `paddle_subscriptions`) and refuses to downgrade a live paid
     * entitlement. `applied: false` is a normal outcome, not an error — read `reason`.
     *
     * This is the ONE write behind the free-plan choice, the Android paywall mount effect, and
     * "restore purchases": they are the same question once the client's opinion is discarded.
     */
    syncSubscription: (id: string): Promise<SubscriptionSyncResult> => {
      return client.post(apiPath`/organizations/${id}/subscription/sync`, {
        data: {},
        responseSchema: SubscriptionSyncResultSchema,
      });
    },

    /**
     * ATL-855 — Atlas-staff subscription override. Requires `users.is_super_admin`; the per-org
     * `admin` role is explicitly NOT sufficient (the merchant is the admin of their own org).
     */
    overrideSubscription: (
      id: string,
      data: OverrideSubscription,
    ): Promise<SubscriptionSyncResult> => {
      return client.patch(apiPath`/organizations/${id}/subscription/override`, {
        data,
        requestSchema: OverrideSubscriptionSchema,
        responseSchema: SubscriptionSyncResultSchema,
      });
    },

    /**
     * Soft-delete an organization
     */
    deleteOrganization: (id: string): Promise<{ message: string }> => {
      return client.delete(apiPath`/organizations/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    /**
     * Compare organization data between Firestore and Supabase
     */
    compareWithFirestore: (firestoreId: string): Promise<OrganizationComparisonResult> => {
      return client.get(apiPath`/organizations/compare/${firestoreId}`, {
        responseSchema: OrganizationComparisonResultSchema,
      });
    },

    /**
     * Reset organization data before a cutoff date
     */
    resetData: (data: {
      cutoffDate: string;
      resetProductStocks?: boolean;
      deleteAllProducts?: boolean;
    }): Promise<z.infer<typeof ResetDataResponseDto>> => {
      return client.post('/organizations/reset-data', {
        data,
        responseSchema: ResetDataResponseDto,
      });
    },
  };
}
